import { effect, inject, Injectable, signal } from '@angular/core';
import { RisikoOpkraevning } from '../risiko-opkraevning.model';
import { RisikoOpkraevningApi } from '../risikoopkraevning-opkraevninger/risiko-opkraevning.api';
import { OpkraevningUdestaaendeSearchParams } from './opkraevning-udestaaende-search/opkraevning-udestaaende-search-params.model';
import { OpkraevningUdestaaendeDatasource } from './opkraevning-udestaaende.datasource';
import { RisikoOpkraevningUdestaaende } from './risiko-opkraevning-udestaaende.model';
import { OpkraevningUdestaaendeApi } from './opkraevning-udestaaende.api';
import { AuthService } from '@core/auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class OpkraevningUdestaaendeService {
  authService = inject(AuthService);
  datasource = inject(OpkraevningUdestaaendeDatasource);
  risikoOpkraevningApi = inject(RisikoOpkraevningApi);
  opkraevningUdestaaendeApi = inject(OpkraevningUdestaaendeApi);
  tilknyttetOpkraevning = signal<RisikoOpkraevning | undefined>(undefined);

  constructor() {
    effect(() => {
      if (this.datasource.isLoaded()) {
        const data = this.datasource.data();
        if (data && data.totalRowCount > 0) {
          this.activeTab.set('result');
        }
      }
    });
  }

  selectedUdestaaende = signal<RisikoOpkraevningUdestaaende | undefined>(
    undefined
  );
  activeTab = signal('search');
  mode = signal('update');

  searchOpkraevningUdestaaede(
    searchParams: OpkraevningUdestaaendeSearchParams
  ) {
    this.selectedUdestaaende.set(undefined);
    this.datasource.searchParams.set(searchParams);
    this.datasource.loadData();
  }

  async selectUdestaaendeResultRow(udestaaende: RisikoOpkraevningUdestaaende) {
    this.mode.set('update');
    await this.selectUdestaaende(udestaaende);

    if (udestaaende) {
      this.activeTab.set('detail');
    }
  }

  async selectUdestaaende(udestaaende: RisikoOpkraevningUdestaaende) {
    const r = await this.datasource.getById(udestaaende.id!);
    this.selectedUdestaaende.set(r);
    await this.indlaesTilknyttetOpkraevning();
  }

  async reloadUdestaaende() {
    if (this.selectedUdestaaende()) {
      await this.selectUdestaaende(this.selectedUdestaaende()!);
    }
  }

  async gemBeloeb(nytBeloeb: number) {
    const newData = {
      ...this.tilknyttetOpkraevning(),
      opkraevningsBeloeb: nytBeloeb || 0,
    } as RisikoOpkraevning;
    await this.risikoOpkraevningApi.update(newData.id, newData);
    await this.indlaesTilknyttetOpkraevning();
  }

  async gemPeriode(
    periodeFra: string | undefined,
    periodeTil: string | undefined
  ) {
    const newData = {
      ...this.tilknyttetOpkraevning(),
      periodeFra,
      periodeTil,
    } as RisikoOpkraevning;
    await this.risikoOpkraevningApi.update(newData.id, newData);
    await this.indlaesTilknyttetOpkraevning();
  }

  async angivSomBehandlet(kommentar: string) {
    const newData = {
      ...this.selectedUdestaaende(),
      behandlingsKommentar: kommentar,
      behandletAf: this.authService.currentUser().email,
      behandlet: new Date().toISOString(),
    } as RisikoOpkraevningUdestaaende;
    await this.opkraevningUdestaaendeApi.update(newData.id, newData);
  }

  async tilknytOpkraevning(risikoOpkraevningId: number) {
    const newData = {
      ...this.selectedUdestaaende(),
      opkraevningReference: risikoOpkraevningId + '',
    } as RisikoOpkraevningUdestaaende;
    await this.opkraevningUdestaaendeApi.update(newData.id, newData);
  }

  async indlaesTilknyttetOpkraevning() {
    const opkraevningReference =
      this.selectedUdestaaende()?.opkraevningReference;
    if (opkraevningReference) {
      const id = parseInt(opkraevningReference);
      const tilknyttet = await this.risikoOpkraevningApi.getById(id);
      this.tilknyttetOpkraevning.set(tilknyttet);
    }
  }

  async hentMuligeOpkraevninger() {
    const response = await this.risikoOpkraevningApi.getList({
      filter: JSON.stringify({
        letsikringNr: this.selectedUdestaaende()?.letsikringNr
      }),
      sortBy:'periodeFra'
    });
    if (response.success) {
      return response.data.filter(
        x =>
          x.opkraevningStatus === 'Ny' || x.opkraevningStatus === 'ClearingFejl'
      );
    }
    return [];
  }
}
